/**
 * Dual-handle price range slider for the price facet.
 *
 * Two overlapped native range inputs drive the visual state. While dragging,
 * only the fill track and the min/max text fields update — the facet's
 * `change` (which triggers a results fetch) fires ONCE, on release. Handles
 * clamp against each other so they can never cross, however hard they're
 * dragged.
 *
 * At-the-bounds values clear the corresponding text field so the URL stays
 * clean (no ?price_min=0&price_max=<max> noise).
 */
class PriceRangeSlider extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLInputElement | null} */
    this.lo = this.querySelector('.price-slider__thumb--min');
    /** @type {HTMLInputElement | null} */
    this.hi = this.querySelector('.price-slider__thumb--max');
    /** @type {HTMLElement | null} */
    this.fill = this.querySelector('.price-slider__fill');

    const facet = this.closest('price-facet-component');
    /** @type {HTMLInputElement | null} */
    this.minField = facet ? facet.querySelector('[ref="minInput"]') : null;
    /** @type {HTMLInputElement | null} */
    this.maxField = facet ? facet.querySelector('[ref="maxInput"]') : null;

    if (!this.lo || !this.hi || !this.fill) return;

    this.step = Number(this.lo.step) || 10;

    this.lo.addEventListener('input', () => this.#onDrag(this.lo));
    this.hi.addEventListener('input', () => this.#onDrag(this.hi));
    // The thumbs' own change events are internal; only the synthesized field
    // change may reach the facet component (otherwise every release fetches twice).
    this.lo.addEventListener('change', (event) => {
      event.stopPropagation();
      this.#commit();
    });
    this.hi.addEventListener('change', (event) => {
      event.stopPropagation();
      this.#commit();
    });

    // Typing in the fields moves the handles.
    this.minField?.addEventListener('change', () => this.#syncFromFields());
    this.maxField?.addEventListener('change', () => this.#syncFromFields());

    this.#paint();
  }

  /** @param {HTMLInputElement | null} moved */
  #onDrag(moved) {
    if (!this.lo || !this.hi) return;
    // Never let the handles cross, keep at least one step between them.
    const gap = this.step;
    if (moved === this.lo && Number(this.lo.value) > Number(this.hi.value) - gap) {
      this.lo.value = String(Math.max(Number(this.lo.min), Number(this.hi.value) - gap));
    }
    if (moved === this.hi && Number(this.hi.value) < Number(this.lo.value) + gap) {
      this.hi.value = String(Math.min(Number(this.hi.max), Number(this.lo.value) + gap));
    }
    this.#reflectIntoFields(false);
    this.#paint();
  }

  /** Writes handle values into the text fields; dispatches when asked. */
  #reflectIntoFields(dispatch) {
    if (!this.lo || !this.hi) return;
    const lo = Number(this.lo.value);
    const hi = Number(this.hi.value);
    const atMin = lo <= Number(this.lo.min);
    const atMax = hi >= Number(this.hi.max);

    if (this.minField) this.minField.value = atMin ? '' : String(lo);
    if (this.maxField) this.maxField.value = atMax ? '' : String(hi);

    if (dispatch) {
      // One change event -> one results fetch, exactly like typing a value.
      (this.maxField ?? this.minField)?.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #commit() {
    this.#reflectIntoFields(true);
  }

  #syncFromFields() {
    if (!this.lo || !this.hi) return;
    const lo = parseFloat((this.minField?.value || '').replace(/[^\d.]/g, ''));
    const hi = parseFloat((this.maxField?.value || '').replace(/[^\d.]/g, ''));
    this.lo.value = String(Number.isFinite(lo) ? lo : Number(this.lo.min));
    this.hi.value = String(Number.isFinite(hi) ? hi : Number(this.hi.max));
    this.#onDrag(null);
  }

  #paint() {
    if (!this.lo || !this.hi || !this.fill) return;
    const min = Number(this.lo.min);
    const max = Number(this.hi.max);
    const span = max - min || 1;
    const loPct = ((Number(this.lo.value) - min) / span) * 100;
    const hiPct = ((Number(this.hi.value) - min) / span) * 100;
    this.fill.style.insetInlineStart = loPct + '%';
    this.fill.style.insetInlineEnd = 100 - hiPct + '%';
  }
}

if (!customElements.get('price-range-slider')) {
  customElements.define('price-range-slider', PriceRangeSlider);
}
